import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import { functions } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const TipModal = ({ artistId, artistName, songId, autoOpened = false, initialAmount = "", initialMessage = "", autoPay = false, onClose }) => {
    const [amount, setAmount] = useState(initialAmount ? String(initialAmount) : "");
    const [message, setMessage] = useState(initialMessage);
    const [paymentStarted, setPaymentStarted] = useState(autoOpened);
    const [showReturnPopup, setShowReturnPopup] = useState(false);
    const wasBackgroundRef = useRef(false);
    const visibilityTimeoutRef = useRef(null);
    const isMountedRef = useRef(false);

    useEffect(() => {
        setAmount(initialAmount ? String(initialAmount) : "");
        setMessage(initialMessage);
        setPaymentStarted(autoOpened);
        setShowReturnPopup(false);
        wasBackgroundRef.current = false;
    }, [artistId, songId, autoOpened, initialAmount, initialMessage]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!autoPay || !paymentStarted || !isMountedRef.current) return;
        openUPI();
        // autoPay is used only after a user taps a quick-tip preset.
        // Razorpay may still require the visible modal on some browsers.
    }, [autoPay, paymentStarted]);

    useEffect(() => {
        if (!paymentStarted) return;

        const onReturn = () => {
            if (!wasBackgroundRef.current || !paymentStarted) return;
            if (visibilityTimeoutRef.current) {
                clearTimeout(visibilityTimeoutRef.current);
            }
            visibilityTimeoutRef.current = setTimeout(() => {
                setShowReturnPopup(true);
            }, 300);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                wasBackgroundRef.current = true;
            } else {
                onReturn();
            }
        };

        const handlePageShow = () => onReturn();

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", onReturn);
        window.addEventListener("pageshow", handlePageShow);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", onReturn);
            window.removeEventListener("pageshow", handlePageShow);
            if (visibilityTimeoutRef.current) {
                clearTimeout(visibilityTimeoutRef.current);
            }
        };
    }, [paymentStarted]);

    const openUPI = async () => {
        // Validation check - ensure we have valid IDs
        if (!artistId || typeof artistId !== "string" || artistId.trim().length === 0) {
            alert("Error: Cannot determine artist. Please try again.");
            return;
        }
        
        if (!songId || typeof songId !== "string" || songId.trim().length === 0) {
            alert("Error: Cannot determine song. Please try again.");
            return;
        }

        if (!auth.currentUser) {
            alert("Please sign in before sending a tip.");
            return;
        }
        const tipAmount = Math.floor(Number(amount));
        if (!Number.isInteger(tipAmount) || tipAmount < 1 || tipAmount > 10000) {
            alert("Tip amount must be a whole number between ₹1 and ₹10,000.");
            return;
        }
        console.groupCollapsed('[HubTune Tip] Preparing Razorpay order');
        console.table({
            songId,
            artistId,
            receiverId: artistId,
            artistName,
            listenerUid: auth.currentUser.uid,
            amount: tipAmount,
            message,
        });
        console.groupEnd();
        try {
            const loadScript = () => new Promise((resolve, reject) => {
                if (window.Razorpay) return resolve();
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = resolve;
                script.onerror = () => reject(new Error("Unable to load Razorpay checkout."));
                document.body.appendChild(script);
            });
            await loadScript();
            const createOrder = httpsCallable(functions, "createRazorpayOrder");
            const { data } = await createOrder({
                amount: tipAmount,
                receiverId: artistId,
                songId: songId,
                message,
            });
            console.groupCollapsed('[HubTune Tip] Razorpay order created');
            console.table({
                sentSongId: songId,
                sentReceiverId: artistId,
                returnedTransactionId: data?.transactionId,
                returnedOrderId: data?.orderId,
            });
            console.groupEnd();
            const checkout = new window.Razorpay({
                key: data.keyId,
                amount: tipAmount * 100,
                currency: "INR",
                name: "HubTune",
                description: `Tip for ${artistName || "Artist"}`,
                order_id: data.orderId,
                handler: () => {
                    setShowReturnPopup(true);
                },
                modal: { ondismiss: () => setPaymentStarted(false) },
            });
            checkout.open();
            setPaymentStarted(true);
        } catch (err) {
            console.error("Failed to start Razorpay payment:", err);
            alert(err?.message || "Failed to start payment. Please try again.");
            setPaymentStarted(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div
                className="bg-[#111] border border-red-900/30 rounded-2xl p-6 w-full max-w-md mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-xl text-white font-semibold mb-4">Support Artist</h2>

                {showReturnPopup ? (
                    <>
                        <div className="mb-4 text-center">
                            <div className="text-green-400 text-4xl mb-2">✓</div>
                            <p className="text-white font-semibold text-lg mb-2">Back on HubTune</p>
                            <p className="text-white/70 text-sm mb-3">
                                Razorpay has received your payment. Webhook verification is in progress.
                            </p>
                        </div>
                        <button
                        onClick={onClose}
                        className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold text-lg"
                        >
                        Done
                        </button>
                    </>
                ) : (
                    <>
                        <p className="text-white/80 text-sm mb-4">
                            Choose UPI in Razorpay Checkout to open your preferred UPI app. Payment is confirmed automatically by webhook.
                        </p>
                        <p className="text-white/50 text-xs mb-4 text-center">
                            Amount: ₹{amount || "0"}
                        </p>
                        <input
                            type="number"
                            min="1"
                            max="10000"
                            step="1"
                            placeholder="Tip amount (₹1–10,000)"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white mb-3"
                        />
                        <textarea
                            placeholder="Message (optional)"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white mb-3 resize-none"
                            rows={2}
                        />
                        <button
                            onClick={openUPI}
                            className="w-full bg-red-500 text-white py-3 rounded-xl font-semibold text-lg mb-2"
                        >
                            Pay with Razorpay / UPI
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full bg-gray-600 text-white py-2 rounded-xl text-sm"
                        >
                            Close
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default TipModal;
